/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  PersonalChargerInitialProperties,
  PersonalChargerResponseInterface,
  RequestPersonalChargerInterFace,
} from '../../interface/end-consumer-personal-charger-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import accountApi from '../../utils/api/account';
import endConsumerApiV2 from '../../utils/api/end-consumer-v2';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchPersonalCharger = createAsyncThunk<
  PersonalChargerResponseInterface,
  RequestPersonalChargerInterFace
>(
  'by-transaction/fetchPersonalCharger',
  async (data: RequestPersonalChargerInterFace, { dispatch }) => {
     const { userCustomId, ...query } = data;
    if (!userCustomId) {
      throw new Error('userCustomId is required');
    }
    try {
      const response = await endConsumerApiV2.fetchPersonalChargers(
        userCustomId,
        removeEmptyKeys(query),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

export const fetchPersonalChargerFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'by-transaction/fetchPersonalChargerFilters',
  async (_, { dispatch }) => {
    try {
      const response = await accountApi.fetchAccountsListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: PersonalChargerInitialProperties = {
  personalCharger: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const PersonalChargerSlice = createSlice({
  name: 'account-Devices',
  initialState,
  reducers: {
    /**
     * Update By Transaction filters
     * @param state
     * @param action
     */
    updatePersonalChargerFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },

    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disablePersonalChargerFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<PersonalChargerInitialProperties>) => {
    builder
      .addCase(fetchPersonalCharger.pending, (state: PersonalChargerInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchPersonalCharger.fulfilled,
        (
          state: PersonalChargerInitialProperties,
          action: PayloadAction<PersonalChargerResponseInterface>,
        ) => {
          state.loading = false;
          state.personalCharger = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchPersonalCharger.rejected, (state: PersonalChargerInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchPersonalChargerFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchPersonalChargerFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchPersonalChargerFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const { updatePersonalChargerFilters, disablePersonalChargerFilterApplied,setSortData } =
  PersonalChargerSlice.actions;
// Export the reducer
export default PersonalChargerSlice.reducer;
